'use client';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
// next/navigation hooks removed for R2 bundle compatibility - using window APIs instead
import { calculateBreachCost } from '@/lib/calculators/breach-cost-calculator';
import { BreachOrganizationProfile, BreachDataProfile, BreachSecurityPosture, BreachCostResult, IndustrySector, GeographicRegion, DataType } from '@/types/calculator';
import { SelectInput, MultiSelect, SliderInput, DualInputWithSlider } from './FormInputs';
import { CalculatorCard, CalculatorSection } from './CalculatorCard';
import { MetricCard, BreakdownTable, ComparisonCard } from './ResultsDisplay';
import { BarChart } from './Charts';
import { InfoIcon } from './Tooltip';
import { decodeURLParams, importBudgetToBreachCost, exportBreachCostToROI, hasIntegrationParams } from '@/lib/calculators/calculator-integration';
import { INDUSTRY_BREACH_LIFECYCLE, INDUSTRY_MULTIPLIERS, REGION_MULTIPLIERS, MATURITY_MULTIPLIERS } from '@/lib/calculators/breach-cost-constants';
import { generateBreachCostPDF } from '@/lib/calculators/breach-cost-pdf';
import { Calculator, Shield } from 'lucide-react';
import { CyberInsuranceTab } from './CyberInsuranceTab';
import { LeadCaptureForm } from './LeadCaptureForm';
import ShareableLinkButton from '@/components/ShareableLinkButton';

const TOOL_SLUG = 'data-breach-cost-calculator';
const industryOptions: {
    value: IndustrySector;
    label: string;
}[] = [
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'pharmaceutical', label: 'Pharmaceutical' },
    { value: 'financial', label: 'Financial Services' },
    { value: 'technology', label: 'Technology' },
    { value: 'retail', label: 'Retail' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'energy', label: 'Energy & Utilities' },
    { value: 'education', label: 'Education' },
    { value: 'government', label: 'Government' },
    { value: 'professional', label: 'Professional Services' }
];
const regionOptions: {
    value: GeographicRegion;
    label: string;
}[] = [
    { value: 'united_states', label: 'United States' },
    { value: 'canada', label: 'Canada' },
    { value: 'uk', label: 'United Kingdom' },
    { value: 'germany', label: 'Germany' },
    { value: 'france', label: 'France' },
    { value: 'middle_east', label: 'Middle East' },
    { value: 'asia_pacific', label: 'Asia Pacific' },
    { value: 'latin_america', label: 'Latin America' }
];
const dataTypeOptions: {
    value: DataType;
    label: string;
    description: string;
}[] = [
    { value: 'customer_pii', label: 'Personal Information (PII)', description: 'Names, addresses, contact info' },
    { value: 'phi', label: 'Protected Health Info (PHI)', description: 'Medical records, HIPAA data' },
    { value: 'pci', label: 'Payment Card Data (PCI)', description: 'Credit cards, PCI DSS data' },
    { value: 'ssn_tax_id', label: 'SSN / Tax IDs', description: 'Social security numbers, EINs' },
    { value: 'ip_trade_secrets', label: 'Intellectual Property', description: 'Trade secrets, patents' },
    { value: 'credentials', label: 'Credentials', description: 'Passwords, API keys' },
    { value: 'financial_records', label: 'Financial Records', description: 'Banking, financial data' }
];
const maturityOptions = [
    { value: 'minimal', label: 'Minimal - Basic security only' },
    { value: 'basic', label: 'Basic - Some controls in place' },
    { value: 'moderate', label: 'Moderate - Documented program' },
    { value: 'advanced', label: 'Advanced - Comprehensive security' },
    { value: 'optimized', label: 'Optimized - Continuous improvement' }
];
type TabType = 'breach-cost' | 'cyber-insurance';

export function BreachCostCalculatorClient() {
    // Use window.location.search instead of useSearchParams for R2 bundle compatibility
    const getSearchParams = useCallback(() => {
        if (typeof window === 'undefined') return new URLSearchParams();
        return new URLSearchParams(window.location.search);
    }, []);

    const searchParams = useMemo(() => getSearchParams(), [getSearchParams]);

    // Replace router.replace with window.history for R2 bundle compatibility
    const updateURL = useCallback((params: URLSearchParams) => {
        if (typeof window === 'undefined') return;
        const newURL = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState(window.history.state, '', newURL);
    }, []);

    // Navigate to a new URL (replaces router.push)
    const navigateTo = useCallback((url: string) => {
        if (typeof window === 'undefined') return;
        window.location.href = url;
    }, []);

    const [activeTab, setActiveTab] = useState<TabType>('breach-cost');
    const [step, setStep] = useState(1);
    const [showResults, setShowResults] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    // Organization profile
    const [industry, setIndustry] = useState<IndustrySector>('technology');
    const [region, setRegion] = useState<GeographicRegion>('united_states');
    const [employeeCount, setEmployeeCount] = useState(100);
    const [annualRevenue, setAnnualRevenue] = useState(5000000); // $5M default
    // Data profile
    const [recordCount, setRecordCount] = useState(50000);
    const [dataTypes, setDataTypes] = useState<DataType[]>(['customer_pii']);
    const [regulatedData, setRegulatedData] = useState(50);
    const [hasSetIndustryDefaults, setHasSetIndustryDefaults] = useState(false);
    const [hasSetRecordDefaults, setHasSetRecordDefaults] = useState(false);
    const [hasSetSecurityDefaults, setHasSetSecurityDefaults] = useState(false);
    // Security posture
    const [maturityLevel, setMaturityLevel] = useState<'minimal' | 'basic' | 'moderate' | 'advanced' | 'optimized'>('basic');
    const [mttd, setMttd] = useState(194); // Mean time to detect
    const [mttc, setMttc] = useState(64); // Mean time to contain
    const [hasIR, setHasIR] = useState(false);
    const [hasInsurance, setHasInsurance] = useState(false);
    const [result, setResult] = useState<BreachCostResult | null>(null);
    // Set industry-specific defaults for data types
    useEffect(() => {
        if (!isInitialized || hasSetIndustryDefaults)
            return;
        const industryDefaults: Record<IndustrySector, DataType[]> = {
            healthcare: ['customer_pii', 'phi'],
            pharmaceutical: ['customer_pii', 'phi', 'ip_trade_secrets'],
            financial: ['customer_pii', 'pci', 'financial_records', 'ssn_tax_id'],
            technology: ['customer_pii', 'ip_trade_secrets', 'credentials'],
            retail: ['customer_pii', 'pci'],
            manufacturing: ['customer_pii', 'ip_trade_secrets'],
            energy: ['customer_pii', 'credentials'],
            industrial: ['customer_pii', 'ip_trade_secrets'],
            education: ['customer_pii', 'ssn_tax_id'],
            government: ['customer_pii', 'ssn_tax_id', 'credentials'],
            professional: ['customer_pii', 'financial_records'],
            hospitality: ['customer_pii', 'pci'],
            media: ['customer_pii', 'ip_trade_secrets'],
            other: ['customer_pii']
        };
        const defaults = industryDefaults[industry];
        if (defaults) {
            setDataTypes(defaults);
            // Set regulated data percentage based on industry
            const regulatedPercentages: Record<IndustrySector, number> = {
                healthcare: 90,
                pharmaceutical: 80,
                financial: 85,
                technology: 30,
                retail: 60,
                manufacturing: 20,
                energy: 40,
                industrial: 20,
                education: 70,
                government: 80,
                professional: 50,
                hospitality: 60,
                media: 30,
                other: 40
            };
            setRegulatedData(regulatedPercentages[industry] || 50);
        }
        setHasSetIndustryDefaults(true);
    }, [industry, isInitialized, hasSetIndustryDefaults]);
    // Reset defaults flag when industry changes (only after initialization)
    useEffect(() => {
        if (isInitialized) {
            setHasSetIndustryDefaults(false);
            setHasSetRecordDefaults(false);
            setHasSetSecurityDefaults(false);
        }
    }, [industry, isInitialized]);
    // Set intelligent record count defaults based on industry and company size
    // Source: Industry research and typical customer/patient/user ratios
    useEffect(() => {
        if (!isInitialized || hasSetRecordDefaults)
            return;
        // Calculate company size category
        const companySize = employeeCount < 250 ? 'small' : employeeCount < 1000 ? 'medium' : employeeCount < 5000 ? 'large' : 'enterprise';
        // Industry-specific record count defaults based on company size
        // Based on typical customer-to-employee ratios and industry research
        const recordDefaults: Record<IndustrySector, Record<string, number>> = {
            healthcare: {
                small: 10000, // Small clinics: ~40-100 patients per employee
                medium: 75000, // Mid-size hospitals: ~75-100 patients per employee
                large: 350000, // Large health systems: ~70-100 patients per employee
                enterprise: 2000000 // Major health systems: 50M+ patient records
            },
            pharmaceutical: {
                small: 5000, // Clinical trials, research participants
                medium: 50000,
                large: 250000,
                enterprise: 1500000
            },
            financial: {
                small: 15000, // Small banks/credit unions: ~60-150 customers per employee
                medium: 100000, // Regional banks: ~100-150 customers per employee
                large: 500000, // Large banks: ~100-150 customers per employee
                enterprise: 5000000 // Major financial institutions: millions of account holders
            },
            technology: {
                small: 25000, // SaaS companies: high user-to-employee ratios (250-1000:1)
                medium: 200000, // Growing tech companies
                large: 1500000, // Large tech companies
                enterprise: 10000000 // Major tech platforms (Facebook, Google scale)
            },
            retail: {
                small: 30000, // Small retailers: ~100-300 customers per employee
                medium: 200000, // Regional retailers
                large: 1000000, // Large retailers
                enterprise: 20000000 // Major retailers (Target, Walmart scale)
            },
            manufacturing: {
                small: 5000, // B2B focused, fewer customer records
                medium: 25000,
                large: 100000,
                enterprise: 500000
            },
            energy: {
                small: 10000, // Utility customers
                medium: 75000,
                large: 400000,
                enterprise: 2000000
            },
            education: {
                small: 3000, // Small schools: students + staff
                medium: 25000, // Universities: ~25-50 per employee
                large: 100000, // Large universities
                enterprise: 500000 // University systems
            },
            government: {
                small: 10000, // Local government: citizens served
                medium: 100000, // County/regional
                large: 500000, // State agencies
                enterprise: 5000000 // Federal agencies
            },
            professional: {
                small: 5000, // Professional services: ~20-50 clients per employee
                medium: 30000,
                large: 150000,
                enterprise: 750000
            },
            hospitality: {
                small: 15000, // Hotels/restaurants: guest records
                medium: 100000,
                large: 500000,
                enterprise: 5000000
            },
            media: {
                small: 20000, // Subscribers/viewers
                medium: 150000,
                large: 1000000,
                enterprise: 10000000
            },
            industrial: {
                small: 5000,
                medium: 25000,
                large: 100000,
                enterprise: 500000
            },
            other: {
                small: 10000,
                medium: 50000,
                large: 250000,
                enterprise: 1000000
            }
        };
        const defaultRecords = recordDefaults[industry]?.[companySize] || 50000;
        setRecordCount(defaultRecords);
        setHasSetRecordDefaults(true);
    }, [industry, employeeCount, isInitialized, hasSetRecordDefaults]);
    // Set intelligent MTTD/MTTC defaults based on industry and company size
    // Source: IBM Cost of a Data Breach Report 2024 + company size research
    // Larger companies have better security teams and faster detection/response times
    useEffect(() => {
        if (!isInitialized || hasSetSecurityDefaults)
            return;
        // Calculate company size category
        const companySize = employeeCount < 250 ? 'small' : employeeCount < 1000 ? 'medium' : employeeCount < 5000 ? 'large' : 'enterprise';
        // Get industry baseline from IBM data
        const industryBaseline = INDUSTRY_BREACH_LIFECYCLE[industry] || {
            mttd: 194,
            mttc: 64,
            total: 258
        };
        // Company size modifiers based on security maturity research:
        // Small companies: +25% (limited security resources, slower detection)
        // Medium companies: +10% (small security team, moderate capability)
        // Large companies: baseline (dedicated security teams)
        // Enterprise companies: -15% (SOCs, advanced monitoring, faster response)
        const sizeModifiers: Record<string, number> = {
            small: 1.25, // +25% slower
            medium: 1.10, // +10% slower
            large: 1.00, // baseline
            enterprise: 0.85 // -15% faster
        };
        const modifier = sizeModifiers[companySize] || 1.0;
        // Apply size modifier to industry-specific times
        const defaultMTTD = Math.round(industryBaseline.mttd * modifier);
        const defaultMTTC = Math.round(industryBaseline.mttc * modifier);
        setMttd(defaultMTTD);
        setMttc(defaultMTTC);
        setHasSetSecurityDefaults(true);
    }, [industry, employeeCount, isInitialized, hasSetSecurityDefaults]);
    // Load URL parameters on mount
    useEffect(() => {
        if (searchParams && !isInitialized) {
            const params = decodeURLParams(searchParams);
            // Check if coming from another calculator
            if (hasIntegrationParams(searchParams)) {
                const importedData = importBudgetToBreachCost(params);
                if (importedData) {
                    if (importedData.industry)
                        setIndustry(importedData.industry);
                    if (importedData.employeeCount)
                        setEmployeeCount(importedData.employeeCount);
                }
            }
            // Load all URL parameters
            if (params.industry)
                setIndustry(params.industry as IndustrySector);
            if (params.region)
                setRegion(params.region as GeographicRegion);
            if (params.employees)
                setEmployeeCount(parseInt(params.employees));
            if (params.revenue)
                setAnnualRevenue(parseInt(params.revenue));
            if (params.records)
                setRecordCount(parseInt(params.records));
            if (params.regulatedData)
                setRegulatedData(parseFloat(params.regulatedData));
            if (params.maturity)
                setMaturityLevel(params.maturity as 'minimal' | 'basic' | 'moderate' | 'advanced' | 'optimized');
            if (params.mttd)
                setMttd(parseInt(params.mttd));
            if (params.mttc)
                setMttc(parseInt(params.mttc));
            if (params.hasIR)
                setHasIR(params.hasIR === 'true');
            if (params.hasInsurance)
                setHasInsurance(params.hasInsurance === 'true');
            if (params.dataTypes) {
                try {
                    setDataTypes(JSON.parse(params.dataTypes));
                    setHasSetIndustryDefaults(true); // Prevent overwriting URL params
                }
                catch (error) {
                    console.error('Failed to parse dataTypes from URL:', error);
                }
            }
            // If URL has records, don't override with defaults
            if (params.records) {
                setHasSetRecordDefaults(true);
            }
            // If URL has MTTD/MTTC, don't override with defaults
            if (params.mttd || params.mttc) {
                setHasSetSecurityDefaults(true);
            }
            setIsInitialized(true);
        }
    }, [searchParams, isInitialized]);
    // Update URL whenever inputs change
    useEffect(() => {
        if (!isInitialized)
            return;
        const params = new URLSearchParams();
        params.set('industry', industry);
        params.set('region', region);
        params.set('employees', String(employeeCount));
        params.set('revenue', String(annualRevenue));
        params.set('records', String(recordCount));
        params.set('regulatedData', String(regulatedData));
        params.set('maturity', maturityLevel);
        params.set('mttd', String(mttd));
        params.set('mttc', String(mttc));
        params.set('hasIR', String(hasIR));
        params.set('hasInsurance', String(hasInsurance));
        params.set('dataTypes', JSON.stringify(dataTypes));
        // Update URL without page reload (using window.history for R2 bundle compatibility)
        updateURL(params);
    }, [
        isInitialized,
        industry,
        region,
        employeeCount,
        annualRevenue,
        recordCount,
        regulatedData,
        maturityLevel,
        mttd,
        mttc,
        hasIR,
        hasInsurance,
        dataTypes,
        updateURL
    ]);
    const calculateResults = () => {
        const org: BreachOrganizationProfile = {
            industry,
            region,
            companySize: employeeCount < 250 ? 'small' : employeeCount < 1000 ? 'medium' : employeeCount < 5000 ? 'large' : 'enterprise',
            annualRevenue
        };
        const data: BreachDataProfile = {
            recordCount,
            dataTypes,
            regulatedDataPercent: regulatedData
        };
        const posture: BreachSecurityPosture = {
            maturityLevel,
            mttd,
            mttc,
            hasIRPlan: hasIR,
            irPlanTested: hasIR,
            hasCyberInsurance: hasInsurance,
            hadRecentBreach: false,
            aiUsage: maturityLevel === 'optimized' || maturityLevel === 'advanced' ? 'extensively' : maturityLevel === 'moderate' ? 'moderately' : 'minimally',
            cloudEnvironment: 'hybrid',
            remoteWorkforcePercent: 50
        };
        const calculation = calculateBreachCost(org, data, posture);
        setResult(calculation);
        setShowResults(true);
    };
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };
    const formatNumber = (value: number) => {
        return new Intl.NumberFormat('en-US').format(value);
    };
    return (<div className="max-w-7xl mx-auto">
      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveTab('breach-cost')}
            className={`flex items-center gap-2 px-4 py-3 rounded-md font-medium transition-all ${
              activeTab === 'breach-cost'
                ? 'bg-gradient-to-r from-[#2cc3d1] to-[#2813e8] text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Calculator className="w-5 h-5" />
            <span>Breach Cost Calculator</span>
          </button>
          <button
            onClick={() => setActiveTab('cyber-insurance')}
            className={`flex items-center gap-2 px-4 py-3 rounded-md font-medium transition-all ${
              activeTab === 'cyber-insurance'
                ? 'bg-gradient-to-r from-[#2cc3d1] to-[#2813e8] text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Shield className="w-5 h-5" />
            <span>Cyber Insurance</span>
          </button>
        </div>
        <div className="mt-3 text-sm text-gray-600">
          {activeTab === 'breach-cost'
            ? 'Calculate the potential cost of a data breach for your organization'
            : 'Get cyber insurance coverage recommendations based on your risk profile'}
        </div>
      </div>

      {/* Cyber Insurance Tab */}
      {activeTab === 'cyber-insurance' && (
        <CyberInsuranceTab
          industry={industry}
          region={region}
          annualRevenue={annualRevenue}
          recordCount={recordCount}
          maturityLevel={maturityLevel}
          hasIR={hasIR}
          breachCostResult={result}
          dataTypes={dataTypes}
        />
      )}

      {/* Breach Cost Calculator Tab */}
      {activeTab === 'breach-cost' && !showResults ? (<>
          {/* Step Indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between max-w-xl mx-auto">
              {[1, 2, 3].map((s) => (<React.Fragment key={s}>
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 font-bold ${s === step
                    ? 'bg-orange-500 text-white border-orange-500'
                    : s < step
                        ? 'bg-green-500 text-white border-green-500'
                        : 'bg-white text-gray-400 border-gray-300'}`}>
                    {s < step ? '✓' : s}
                  </div>
                  {s < 3 && (<div className={`flex-1 h-1 mx-2 ${s < step ? 'bg-green-500' : 'bg-gray-300'}`}/>)}
                </React.Fragment>))}
            </div>
            <div className="flex justify-between max-w-xl mx-auto mt-2 text-xs font-semibold text-gray-600">
              <span>Organization</span>
              <span>Data</span>
              <span>Security</span>
            </div>
          </div>

          {/* Step 1: Organization */}
          {step === 1 && (<CalculatorCard>
              <CalculatorSection title="Organization Profile" description="Basic information about your organization" icon="🏢">
                <SelectInput label="Industry" value={industry} onChange={(v) => setIndustry(v as IndustrySector)} options={industryOptions} helpText="Industry affects breach costs significantly" required/>

                <SelectInput label="Geographic Region" value={region} onChange={(v) => setRegion(v as GeographicRegion)} options={regionOptions} helpText="Regional regulations impact costs" required/>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DualInputWithSlider label="Number of Employees" value={employeeCount} onChange={setEmployeeCount} min={1} max={50000} helpText="Used to calculate indirect costs" required/>

                  <DualInputWithSlider label="Annual Revenue" value={annualRevenue} onChange={setAnnualRevenue} min={100000} max={10000000000} prefix="$" helpText="Used to calculate business disruption costs" required/>
                </div>
              </CalculatorSection>

              <div className="flex justify-end mt-6">
                <button onClick={() => setStep(2)} className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors">
                  Next: Data Profile
                </button>
              </div>
            </CalculatorCard>)}

          {/* Step 2: Data Profile */}
          {step === 2 && (<CalculatorCard>
              <CalculatorSection title="Data at Risk" description="What data could be compromised in a breach?" icon="💾">
                <DualInputWithSlider label="Number of Records at Risk" value={recordCount} onChange={setRecordCount} min={100} max={10000000} helpText="Total customer/patient records in your systems. Defaults based on your industry and company size." required/>

                <MultiSelect label="Types of Data Stored" value={dataTypes} onChange={(values) => setDataTypes(values as DataType[])} options={dataTypeOptions} helpText="Select all that apply - affects regulatory fines"/>

                <SliderInput label="Percentage of Regulated Data" value={regulatedData} onChange={setRegulatedData} min={0} max={100} unit="%" helpText="What % of records contain regulated data (HIPAA, GDPR, PCI DSS)?"/>
              </CalculatorSection>

              <div className="flex justify-between mt-6">
                <button onClick={() => setStep(1)} className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold transition-colors">
                  Back
                </button>
                <button onClick={() => setStep(3)} className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors">
                  Next: Security Posture
                </button>
              </div>
            </CalculatorCard>)}

          {/* Step 3: Security Posture */}
          {step === 3 && (<CalculatorCard>
              <CalculatorSection title="Security Posture" description="Your current security capabilities" icon="🛡️">
                <SelectInput label="Security Maturity Level" value={maturityLevel} onChange={(v) => setMaturityLevel(v as 'minimal' | 'basic' | 'moderate' | 'advanced' | 'optimized')} options={maturityOptions} helpText="Higher maturity = lower breach costs" required/>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DualInputWithSlider label="Mean Time to Detect (MTTD)" value={mttd} onChange={setMttd} min={1} max={365} suffix=" days" helpText="Time to discover a breach. Defaults based on industry and company size." required/>

                  <DualInputWithSlider label="Mean Time to Contain (MTTC)" value={mttc} onChange={setMttc} min={1} max={365} suffix=" days" helpText="Time to fully contain a breach after detection. Defaults based on industry and company size." required/>
                </div>

                <div className="mb-4">
                  <label className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                    <input type="checkbox" checked={hasIR} onChange={(e) => setHasIR(e.target.checked)} className="w-5 h-5 text-orange-500 border-gray-300 rounded focus:ring-2 focus:ring-orange-200"/>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900 flex items-center gap-2">
                        Have Incident Response Plan
                        <InfoIcon tooltip="A documented and tested incident response (IR) plan outlines step-by-step procedures for detecting, containing, and recovering from security incidents. Organizations with tested IR plans save an average of $1.49M (25% cost reduction) according to IBM's 2024 research. The plan should include roles, communication protocols, containment procedures, and be tested at least annually through tabletop exercises or simulations."/>
                      </div>
                      <div className="text-sm text-gray-600">
                        Tested IR plan reduces breach costs by 25%
                      </div>
                    </div>
                  </label>
                </div>

                <div className="mb-4">
                  <label className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                    <input type="checkbox" checked={hasInsurance} onChange={(e) => setHasInsurance(e.target.checked)} className="w-5 h-5 text-orange-500 border-gray-300 rounded focus:ring-2 focus:ring-orange-200"/>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900 flex items-center gap-2">
                        Have Cyber Insurance
                        <InfoIcon tooltip="Cyber insurance helps offset breach costs by covering expenses like forensics, legal fees, notification costs, credit monitoring, and business interruption. Typical policies cover 60-80% of total breach costs (this calculator uses 65% average). However, premiums typically increase significantly after a claim (estimated $35K/year for 3 years), and policies have deductibles and coverage limits. Requirements often include MFA, EDR, backups, and other security controls."/>
                      </div>
                      <div className="text-sm text-gray-600">
                        Insurance typically covers 60-80% of breach costs (avg. 65%)
                      </div>
                    </div>
                  </label>
                </div>
              </CalculatorSection>

              <div className="flex justify-between mt-6">
                <button onClick={() => setStep(2)} className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold transition-colors">
                  Back
                </button>
                <button onClick={calculateResults} className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors">
                  Calculate Breach Cost
                </button>
              </div>
            </CalculatorCard>)}
        </>) : activeTab === 'breach-cost' ? (<>
          {/* Results Display */}
          {result && (<>
              <div className="mb-8">
                <button onClick={() => {
                    setShowResults(false);
                    setStep(3); // Go back to the last step for quick edits
                }} className="text-orange-500 hover:text-orange-600 font-semibold flex items-center gap-2">
                  ← Recalculate
                </button>
              </div>

              {/* Alert Banner */}
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="text-3xl">⚠️</div>
                  <div>
                    <h3 className="text-xl font-bold text-red-900 mb-2">
                      Estimated Breach Cost: {formatCurrency(result.totalCost)}
                    </h3>
                    <p className="text-red-700">
                      This is the estimated financial impact of a data breach affecting{' '}
                      {formatNumber(recordCount)} records based on IBM's 2024 Cost of a Data Breach methodology.
                    </p>
                  </div>
                </div>
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <MetricCard label="Total Breach Cost" value={formatCurrency(result.totalCost)} description="Total estimated impact" icon="💸" color="red" tooltip="The total financial impact of a data breach affecting your organization, including all direct costs, legal fees, regulatory fines, business disruption, and post-breach response. This is calculated using IBM's 2024 Cost of a Data Breach methodology with your specific risk factors applied."/>
                <MetricCard label="Cost Per Record" value={formatCurrency(result.costPerRecord)} description="Average per record" icon="📊" color="orange" tooltip={`This cost per record is calculated using IBM's 2024 methodology:

Base Cost: $165 (global average)
× Industry Multiplier (${industry}): ${INDUSTRY_MULTIPLIERS[industry as keyof typeof INDUSTRY_MULTIPLIERS]}×
× Region Multiplier (${region.replace(/_/g, ' ')}): ${REGION_MULTIPLIERS[region as keyof typeof REGION_MULTIPLIERS]}×
× Scale Factor (${formatNumber(recordCount)} records): varies by breach size

= ${formatCurrency(result.costPerRecord)} per record

This accounts for notification, investigation, legal, and remediation costs per compromised record. Larger breaches benefit from economies of scale, while smaller breaches have higher per-record costs.`}/>
                <MetricCard label="Detection + Contain" value={`${result.breachLifecycleDays} days`} description="Time to resolve" icon="⏱️" color="red" tooltip={`Total breach lifecycle from initial compromise to full containment. This includes ${result.timeline.detectionDays} days to detect (MTTD) and ${result.timeline.containmentDays} days to contain (MTTC). Faster detection and response significantly reduce total breach costs. Industry average is 258 days (194 MTTD + 64 MTTC).`}/>
                <MetricCard label="Mitigated Risk" value={formatCurrency(result.costReductionFromControls)} description="Saved by controls" icon="✅" color="green" tooltip="The amount your current security controls are saving you compared to having no security measures in place. This includes savings from your security maturity level, incident response capabilities, detection speed, and other protective measures you have implemented."/>
              </div>

              {/* Final Cost After Mitigation */}
              {hasInsurance && result.insurance.coverage > 0 && (<div className="bg-gradient-to-r from-blue-50 to-green-50 border-2 border-blue-200 rounded-xl p-6 mb-8">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span>🛡️</span>
                    Final Cost After Insurance
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between py-2">
                      <span className="text-gray-700 font-medium">Total Breach Cost</span>
                      <span className="text-lg font-semibold text-gray-900">
                        {formatCurrency(result.totalCost)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-t border-gray-300">
                      <span className="text-gray-700 font-medium">Insurance Coverage (65%)</span>
                      <span className="text-lg font-semibold text-green-600">
                        - {formatCurrency(result.insurance.coverage)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-3 border-t-2 border-blue-300 bg-white rounded-lg px-4 -mx-2">
                      <span className="text-gray-900 font-bold text-lg">Your Out-of-Pocket Cost</span>
                      <span className="text-2xl font-bold text-blue-600">
                        {formatCurrency(result.insurance.outOfPocket)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      ⚠️ Note: Insurance premiums may increase by approximately {formatCurrency(result.insurance.premiumIncrease)} over the next 3 years.
                    </p>
                  </div>
                </div>)}

              {/* Cost Breakdown */}
              <BreakdownTable title="Cost Breakdown by Category" items={[
                    {
                        label: 'Per-Record Costs',
                        value: formatCurrency(result.breakdown.recordCost),
                        tooltip: 'Base cost per compromised record, scaled by industry, region, and breach size. Includes data analysis, customer impact assessment, and initial response.'
                    },
                    {
                        label: 'Detection & Investigation',
                        value: formatCurrency(result.breakdown.detection),
                        tooltip: 'Forensic investigation, security analytics, incident response team, breach assessment, and evidence collection costs.'
                    },
                    {
                        label: 'Notification Costs',
                        value: formatCurrency(result.breakdown.notification),
                        tooltip: 'Customer notifications, credit monitoring services, regulatory notifications, media management, call center setup, and website updates.'
                    },
                    {
                        label: 'Legal & Regulatory',
                        value: formatCurrency(result.breakdown.legal),
                        tooltip: 'Outside counsel, regulatory defense, litigation defense, and potential class action settlement reserves.'
                    },
                    {
                        label: 'Regulatory Fines',
                        value: formatCurrency(result.breakdown.fines),
                        tooltip: 'Potential fines from GDPR, HIPAA, PCI DSS, state privacy laws, and other regulatory frameworks based on your industry and data types.'
                    },
                    {
                        label: 'Business Disruption',
                        value: formatCurrency(result.breakdown.businessDisruption),
                        tooltip: 'Revenue loss during containment, customer churn (3-year LTV), and system downtime costs. Scales with company revenue and containment time.'
                    },
                    {
                        label: 'Post-Breach Response',
                        value: formatCurrency(result.breakdown.postBreach),
                        tooltip: 'Security upgrades, consulting services, employee training, insurance premium increases, brand recovery efforts, and executive time costs.'
                    }
                ]} total={{
                    label: 'Total Estimated Cost',
                    value: formatCurrency(result.totalCost)
                }}/>

              {/* Cost Comparison Chart */}
              <div className="my-8">
                <BarChart title="Breach Cost Comparison" data={[
                    {
                        label: 'Direct Costs',
                        value: result.breakdown.recordCost +
                            result.breakdown.detection +
                            result.breakdown.notification,
                        color: 'bg-orange-500'
                    },
                    {
                        label: 'Legal & Regulatory',
                        value: result.breakdown.legal + result.breakdown.fines,
                        color: 'bg-red-500'
                    },
                    {
                        label: 'Business Impact',
                        value: result.breakdown.businessDisruption + result.breakdown.postBreach,
                        color: 'bg-purple-500'
                    }
                ]} unit="" showValues={true}/>
              </div>

              {/* Cost Mitigation Breakdown */}
              <CalculatorCard className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4" id="cost-mitigation-analysis">
                  Cost Mitigation Analysis
                </h3>

                {/* Base Cost */}
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-gray-900" id="base-breach-cost-no-controls">Base Breach Cost (No Controls)</h4>
                      <p className="text-sm text-gray-600">Cost without any security controls</p>
                    </div>
                    <div className="text-2xl font-bold text-red-600">
                      {formatCurrency(result.costWithoutControls)}
                    </div>
                  </div>
                </div>

                {/* Current Mitigations */}
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="text-green-600">✓</span>
                    Current Security Controls Saving You:
                  </h4>
                  <div className="space-y-2 ml-6">
                    {(() => {
                      const baselineMultiplier = MATURITY_MULTIPLIERS.minimal;
                      const currentMultiplier = MATURITY_MULTIPLIERS[maturityLevel];
                      const savingsPercent = Math.max(0, (baselineMultiplier - currentMultiplier) / baselineMultiplier);
                      if (savingsPercent <= 0) {
                        return null;
                      }
                      const maturityLabel = maturityLevel === 'optimized'
                        ? '🛡️ Optimized Security Maturity'
                        : maturityLevel === 'advanced'
                          ? '🛡️ Advanced Security Maturity'
                          : maturityLevel === 'moderate'
                            ? '🛡️ Moderate Security Maturity'
                            : '🛡️ Basic Security Controls';
                      return (
                        <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                          <span className="text-gray-700">{maturityLabel}</span>
                          <span className="font-semibold text-green-600">
                            -{formatCurrency(result.costWithoutControls * savingsPercent)}
                          </span>
                        </div>
                      );
                    })()}
                    {hasIR && (<div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <span className="text-gray-700">
                          📋 Tested Incident Response Plan
                        </span>
                        <span className="font-semibold text-green-600">
                          -{formatCurrency(result.costWithoutControls * 0.25)}
                        </span>
                      </div>)}
                    {result.breachLifecycleDays < 200 && (<div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <span className="text-gray-700">⚡ Fast Detection & Response ({result.breachLifecycleDays} days)</span>
                        <span className="font-semibold text-green-600">
                          -{formatCurrency(result.costWithoutControls * 0.24)}
                        </span>
                      </div>)}
                  </div>
                  <div className="mt-4 p-4 bg-green-100 border-2 border-green-300 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-gray-900">Total Current Savings</span>
                      <span className="text-2xl font-bold text-green-600">
                        -{formatCurrency(result.costReductionFromControls)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Current Estimated Cost */}
                <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-gray-900" id="current-estimated-breach-cost">Current Estimated Breach Cost</h4>
                      <p className="text-sm text-gray-600">With your current security controls</p>
                    </div>
                    <div className="text-2xl font-bold text-blue-600">
                      {formatCurrency(result.totalCost)}
                    </div>
                  </div>
                </div>

                {/* Additional Opportunities */}
                {result.recommendations && result.recommendations.length > 0 && (<>
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <span className="text-orange-600">→</span>
                      Additional Opportunities to Reduce Costs:
                    </h4>
                    <div className="space-y-3">
                      {result.recommendations.map((rec, index) => (<div key={index} className="p-4 bg-orange-50 border-2 border-orange-200 rounded-lg">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-7 h-7 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <h5 className="font-bold text-gray-900 mb-1">{rec.action}</h5>
                              <p className="text-sm text-gray-700 mb-2">{rec.impact}</p>
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-semibold text-orange-600">
                                  Potential additional savings: {formatCurrency(rec.costSavings)}
                                </span>
                                <span className="text-xs text-gray-600">
                                  Investment: {formatCurrency(rec.investmentNeeded)}/year
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>))}
                    </div>
                  </>)}
              </CalculatorCard>

              {/* Industry Comparison */}
              <CalculatorCard className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4" id="industry-comparison">Industry Comparison</h3>
                <p className="text-gray-600 mb-4">{result.industryComparison}</p>
                <ComparisonCard title="Timeline Impact" before={{
                    label: 'Current Timeline',
                    value: `${result.breachLifecycleDays} days`
                }} after={{
                    label: 'Industry Best Practice',
                    value: '200 days'
                }} savings={{
                    label: 'Potential Cost Reduction',
                    value: formatCurrency(result.costReductionFromControls)
                }}/>
              </CalculatorCard>

              {/* Action Buttons */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <button onClick={() => {
                    generateBreachCostPDF({
                        result,
                        inputs: {
                            industry,
                            region,
                            employeeCount,
                            annualRevenue,
                            recordCount,
                            regulatedData,
                            dataTypes,
                            maturityLevel,
                            mttd,
                            mttc,
                            hasIR,
                            hasInsurance
                        }
                    });
                }} className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors">
                  📄 Download PDF Report
                </button>
                <button onClick={() => {
                    const roiURL = exportBreachCostToROI(industry, employeeCount, result.totalCost, recordCount);
                    navigateTo(roiURL);
                }} className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors">
                  💰 Calculate ROI of Prevention →
                </button>
                <ShareableLinkButton toolSlug={TOOL_SLUG} size="lg" variant="default" />
                <button onClick={() => {
                    setShowResults(false);
                    setStep(1); // Reset to the beginning
                }} className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold transition-colors">
                  ↻ Start Over
                </button>
              </div>

              {/* Lead Capture Form */}
              <div className="mt-12">
                <LeadCaptureForm
                  source="data-breach-cost-calculator"
                  title="Get Expert Risk Reduction Guidance"
                  description="Our security experts will review your breach cost analysis and provide actionable recommendations to reduce your risk exposure and potential financial impact."
                  deliverable="a personalized risk reduction plan"
                  resultsSummary={`DATA BREACH COST ANALYSIS RESULTS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Estimated Total Breach Cost: ${formatCurrency(result.totalCost)}
Cost Per Record: ${formatCurrency(result.costPerRecord)}
Records at Risk: ${recordCount.toLocaleString()}
Industry: ${industryOptions.find(o => o.value === industry)?.label || industry}
Region: ${regionOptions.find(o => o.value === region)?.label || region}
Company Size: ${employeeCount.toLocaleString()} employees
Security Maturity: Level ${maturityLevel}

BREACH TIMELINE:
Detection Time (MTTD): ${mttd} days
Containment Time (MTTC): ${mttc} days
Total Lifecycle: ${result.breachLifecycleDays} days

COST BREAKDOWN:
• Record-Based Costs: ${formatCurrency(result.breakdown.recordCost)}
• Detection & Escalation: ${formatCurrency(result.breakdown.detection)}
• Notification Costs: ${formatCurrency(result.breakdown.notification)}
• Legal & Regulatory: ${formatCurrency(result.breakdown.legal + result.breakdown.fines)}
• Business Disruption: ${formatCurrency(result.breakdown.businessDisruption)}
• Post-Breach Response: ${formatCurrency(result.breakdown.postBreach)}

RISK MITIGATION:
Current Controls Savings: ${formatCurrency(result.costReductionFromControls)}
Has Incident Response Plan: ${hasIR ? 'Yes' : 'No'}
Has Cyber Insurance: ${hasInsurance ? 'Yes' : 'No'}`}
                />
              </div>
            </>)}
        </>) : null}
    </div>);
}
